import React, { useState } from 'react';
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCardTitle,
  CCol,
  CFormGroup,
  CLabel,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CRow,
  CTextarea,
} from '@coreui/react';
import { Link, useParams } from 'react-router-dom';
import {
  loadCustomer,
  loadTransaction,
  refundWithdrawal,
  updateTransactionComment,
  qualifyTransaction,
} from '../services/BackendService';
import { TransactionStatusBadge } from './transactions/TransactionStatusBadge';
import { extractErrorMessage, formatCurrency, formatDateTime, getIconNameForCountry } from '../helpers';
import _t from 'counterpart';
import { useMutation, useQuery } from 'react-query';
import { ITransaction, TransactionStatus, TransactionType } from './transactions/types';
import CIcon from '@coreui/icons-react';
import Loading from '../components/Loading';
import Error from '../components/Error';
import PageLayout from '../components/PageLayout';
import ButtonWrapper from '../components/ButtonWrapper';
import UpdateWithdrawalStateModal from './transactions/UpdateWithdrawalStateModal';
import WithdrawalReviewStatusBadge from './transactions/WithdrawalReviewStatusBadge';
import toast from 'react-hot-toast';
import InlineSpinner from '../components/InlineSpinner';
import { useAppSelector } from '../helpers/customHooks';
import { Permission } from '../reducers/userReducer';

const TransactionPage = () => {
  const params = useParams();

  const { id } = params as any;

  interface ISelectedWithdrawal {
    withdrawal: ITransaction;
    tableActionType: 'approve' | 'reject';
  }

  const [refundedWithdrawal, setRefundedWithdrawal] = useState<string | null>(null);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<ISelectedWithdrawal | null>(null);
  const permissions = useAppSelector((state) => state.user.permissions);
  const [isEditCommentModalOpen, setEditCommentModalOpen] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [isQualifyModalOpen, setQualifyModalOpen] = useState(false);
  const loadTransactionQuery = useQuery(['transaction', id], () => loadTransaction(id || ''), {
    onError: (e: any) => {
      const error = extractErrorMessage(e);
      toast.error(error);
    },
    retry: false,
    enabled: Boolean(id),
  });

  const transaction = loadTransactionQuery.data;
  const withdrawalReviewerId = transaction?.withdrawalReview?.reviewerId;

  const openEditCommentModal = () => {
    if (!transaction) {
      return;
    }
    setCommentDraft(transaction.comment ?? '');
    setEditCommentModalOpen(true);
  };

  const closeEditCommentModal = () => {
    if (updateTransactionCommentMutation.isLoading) {
      return;
    }
    setEditCommentModalOpen(false);
    setCommentDraft('');
  };

  const handleCorrectionCommentSave = () => {
    updateTransactionCommentMutation.mutate(commentDraft.trim());
  };

  const withdrawalReviewerQuery = useQuery(
    ['transaction-reviewer', withdrawalReviewerId],
    () => loadCustomer(withdrawalReviewerId!),
    {
      enabled: Boolean(withdrawalReviewerId) && permissions.includes(Permission.VIEW_TRANSACTIONS),
      retry: false,
      onError: (e: any) => {
        if (permissions.includes(Permission.VIEW_TRANSACTIONS)) {
          const error = extractErrorMessage(e);
          toast.error(`Error loading reviewer: ${error}`);
        }
      },
    }
  );

  const refundMutation = useMutation(['refund'], (transactionId: string) => refundWithdrawal(transactionId), {
    onSuccess: () => {
      toast.success(_t('transactions.successful-refund'));
      loadTransactionQuery.refetch();
    },
    onError: (error: any) => {
      const msg = extractErrorMessage(error);
      toast.error(msg);
    },
  });

  const updateTransactionCommentMutation = useMutation((comment: string) => updateTransactionComment(id, comment), {
    onSuccess: () => {
      toast.success(_t('transactions.comment-updated'));
      loadTransactionQuery.refetch();
      setEditCommentModalOpen(false);
      setCommentDraft('');
    },
    onError: (error: any) => {
      const msg = extractErrorMessage(error);
      toast.error(msg);
    },
  });

  const qualifyMutation = useMutation(() => qualifyTransaction(id), {
    onSuccess: (result) => {
      let message = _t('qualification.transaction-qualified');
      if (result.cpaAwarded) {
        message += ' ' + _t('qualification.cpa-awarded');
      }
      if (result.affiliateFTDSet) {
        message += ' ' + _t('qualification.affiliate-ftd-set');
      }
      toast.success(message);
      loadTransactionQuery.refetch();
      setQualifyModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error));
    },
  });

  const showWithdrawalModal = (tableActionType: 'approve' | 'reject', withdrawal: ITransaction) => {
    setSelectedWithdrawal({ withdrawal, tableActionType });
  };

  const refund = (transactionId: string) => {
    if (!transactionId) return;
    setRefundedWithdrawal(transactionId);
    refundMutation.mutate(transactionId);
  };

  const onWithdrawalChangeStateClose = (refetchData: boolean) => {
    if (refetchData) {
      loadTransactionQuery.refetch();
    }
    setSelectedWithdrawal(null);
  };

  const getCountryIcon = (countryCode: string) => {
    if (!countryCode) return null;
    return <CIcon name={getIconNameForCountry(countryCode.toLowerCase())} size="xl" title={countryCode} />;
  };

  if (loadTransactionQuery.isLoading) {
    return <Loading />;
  }

  if (loadTransactionQuery.isError) {
    return <Error onRetry={loadTransactionQuery.refetch} />;
  }

  if (!transaction) {
    return <Error onRetry={loadTransactionQuery.refetch} />;
  }

  const canEditCorrectionComment =
    transaction.type === TransactionType.Correction && permissions.includes(Permission.MANAGE_TRANSACTIONS);

  const canQualify = permissions.includes(Permission.QUALIFY_AFFILIATE_TRANSACTIONS);
  const isQualifiableType =
    transaction.type === TransactionType.Deposit || transaction.type === TransactionType.PropPurchase;
  const isSuccessful = transaction.status === TransactionStatus.Successful;
  const isQualified = (transaction as any).isQualifiedForAffiliate;

  const normalizedOriginalComment = (transaction.comment ?? '').trim();
  const normalizedCommentDraft = commentDraft.trim();
  const isSaveDisabled =
    updateTransactionCommentMutation.isLoading || normalizedOriginalComment === normalizedCommentDraft;

  return (
    <PageLayout
      title={_t.translate('transaction.title')}
      titleAppend={
        <CRow className="float-right">
          {transaction.type === 'WITHDRAWAL' &&
            transaction.status === TransactionStatus.Pending &&
            permissions.includes(Permission.MANAGE_TRANSACTIONS) && (
              <>
                <CButton onClick={() => showWithdrawalModal('approve', transaction)} className="mr-2" color="success">
                  {_t('action.approve')}
                </CButton>
                <CButton onClick={() => showWithdrawalModal('reject', transaction)} className="mr-2" color="danger">
                  {_t('action.reject')}
                </CButton>
              </>
            )}
          {transaction.type === 'WITHDRAWAL' && transaction.status === TransactionStatus.Successful && (
            <ButtonWrapper
              isLoading={refundMutation.isLoading && refundedWithdrawal === transaction.id}
              onClick={() => refund(transaction.id)}
              className="mr-1"
              color="primary"
            >
              {_t('action.refund')}
            </ButtonWrapper>
          )}
          {canQualify && isQualifiableType && isSuccessful && !isQualified && (
            <CButton onClick={() => setQualifyModalOpen(true)} className="mr-2" color="success">
              {_t('qualification.qualify')}
            </CButton>
          )}
        </CRow>
      }
    >
      <CRow>
        <CCol lg={6}>
          <CCard>
            <CCardHeader>
              <CCardTitle className="float-left">{_t('global.details')}</CCardTitle>
              <div className="float-right">
                <TransactionStatusBadge status={transaction.status || TransactionStatus.Failed} />
              </div>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol md={6}>
                  <dl>
                    <dt>{_t('transaction.transaction-id')}:</dt>
                    <dd>{id}</dd>

                    <dt>{_t('global.customer')}</dt>
                    <dd>
                      {transaction.customer && (
                        <Link to={`/customers/${transaction.customer.id}`}>{transaction.customer.name}</Link>
                      )}
                      {!transaction.customer && '-'}
                    </dd>

                    <dt>{_t('transactions.wallet')}:</dt>
                    <dd>
                      {transaction.wallet && <Link to={`/wallets/${transaction.wallet}`}>{transaction.wallet}</Link>}
                      {!transaction.wallet && '-'}
                    </dd>

                    {transaction.propFunnelId && (
                      <>
                        <dt>{_t('transactions.prop-funnel')}:</dt>
                        <dd>
                          <Link to={`/prop-funnels/${transaction.propFunnelId}`}>{transaction.propFunnelId}</Link>
                        </dd>
                      </>
                    )}

                    <dt>{_t('global.amount')}:</dt>
                    <dd>{formatCurrency(transaction.amount, transaction.currency)}</dd>

                    <dt>{_t('global.net-amount')} USD:</dt>
                    <dd>{formatCurrency(transaction.netAmountInUSD || 0)}</dd>

                    <dt>{_t('transactions.ip-address')}:</dt>
                    <dd>
                      {transaction.ipAddress || '-'}&nbsp;
                      {transaction.countryCode && getCountryIcon(transaction.countryCode)}
                    </dd>

                    {transaction.withdrawalReview && (
                      <>
                        <dt>{_t('transaction.admin-ip-address')}:</dt>
                        <dd>
                          {transaction.withdrawalReview.reviewerIp || '-'}&nbsp;
                          {transaction.reviewerCountryCode && getCountryIcon(transaction.reviewerCountryCode)}
                        </dd>
                      </>
                    )}

                    <dt>{_t('global.created-at')}:</dt>
                    <dd>{transaction.createdAt ? formatDateTime(transaction.createdAt) : '-'}</dd>

                    <dt>{_t('global.credited-at')}:</dt>
                    <dd>{transaction.creditedAt ? formatDateTime(transaction.creditedAt) : '-'}</dd>
                  </dl>
                </CCol>
                <CCol md={6}>
                  <dl>
                    <dt>{_t('global.method')}:</dt>
                    <dd>{transaction.method || 'Internal'}</dd>

                    <dt>{_t('global.type')}:</dt>
                    <dd>{transaction.type ? _t(`transactionTypes.${transaction.type}`, { fallback: transaction.type }) : '-'}</dd>

                    <dt>{_t('global.provider')}:</dt>
                    <dd>{transaction.provider || 'Internal'}</dd>

                    <dt>{_t('global.provider-reference')}:</dt>
                    <dd>{transaction.providerRef || _t('global.no-value')}</dd>

                    {/* The trading-server counterpart to the provider reference, and the only handle
                        that ties this transaction to the movement on MT5. Shown only when there is
                        one: a Wintrado account has no deal, so an empty row would be noise. */}
                    {transaction.mt5DealId && (
                      <>
                        <dt>{_t('global.mt5-deal-id')}:</dt>
                        <dd>{transaction.mt5DealId}</dd>
                      </>
                    )}

                    <dt>{_t('transaction.qualified-at')}:</dt>
                    <dd>{transaction.qualifiedAt ? formatDateTime(transaction.qualifiedAt) : '-'}</dd>

                    <dt>{_t('transaction.qualified-by')}:</dt>
                    <dd>
                      {transaction.qualifiedBy ? (
                        <Link to={`/customers/${transaction.qualifiedById}`}>{transaction.qualifiedBy}</Link>
                      ) : (
                        '-'
                      )}
                    </dd>

                    {transaction.discountCode && (
                      <>
                        <dt>{_t('discount-codes.discount')}:</dt>
                        <dd>
                          <Link to={`/discount-codes/${transaction.discountCode.id}`}>
                            {transaction.discountCode.name} ({transaction.discountCode.code})
                          </Link>
                        </dd>
                      </>
                    )}

                    <dt>{_t('global.comment')}:</dt>
                    <dd>
                      <div className="d-flex align-items-start justify-content-between">
                        <span className="pr-3 text-break">{transaction.comment || '-'}</span>
                        {canEditCorrectionComment && (
                          <CButton color="link" size="sm" className="p-0" onClick={openEditCommentModal}>
                            {_t('action.edit')}
                          </CButton>
                        )}
                      </div>
                    </dd>

                    {transaction.type === 'WITHDRAWAL' && (
                      <>
                        <dt>{_t('global.reviewed-by')}:</dt>
                        <dd>
                          {!withdrawalReviewerId && '-'}
                          {withdrawalReviewerId && withdrawalReviewerQuery.isLoading && <InlineSpinner />}
                          {withdrawalReviewerId && withdrawalReviewerQuery.isSuccess && (
                            <Link to={`/customers/${withdrawalReviewerQuery.data.id}`}>
                              {withdrawalReviewerQuery.data.name}
                            </Link>
                          )}
                        </dd>

                        <dt>{_t('global.review-status')}:</dt>
                        <dd>
                          <WithdrawalReviewStatusBadge status={transaction.withdrawalReview?.status || null} />
                        </dd>
                      </>
                    )}
                  </dl>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol lg={6}>
          <CCard>
            <CCardHeader>
              <CCardTitle>{_t('transactions.exchange-rate-details')}</CCardTitle>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol md={6}>
                  <dl>
                    <dt>{_t('transactions.conversion.from')}:</dt>
                    <dd>{transaction.conversionFrom || '-'}</dd>

                    <dt>{_t('transactions.conversion.from-amount')}:</dt>
                    <dd>
                      {transaction.conversionFrom
                        ? formatCurrency(transaction.conversionFromAmount, transaction.conversionFrom)
                        : '-'}
                    </dd>

                    <dt>{_t('transactions.conversion.rate')}:</dt>
                    <dd>{transaction.conversionRate ? Number(transaction.conversionRate).toFixed(2) : '-'}</dd>

                    <dt>{_t('transactions.conversion.date')}:</dt>
                    <dd>{transaction.conversionDate ? formatDateTime(transaction.conversionDate) : '-'}</dd>
                  </dl>
                </CCol>
                <CCol md={6}>
                  <dl>
                    <dt>{_t('transactions.conversion.to')}:</dt>
                    <dd>{transaction.conversionTo || '-'}</dd>

                    <dt>{_t('transactions.conversion.to-amount')}:</dt>
                    <dd>
                      {transaction.conversionTo
                        ? formatCurrency(transaction.conversionToAmount, transaction.conversionTo)
                        : '-'}
                    </dd>

                    <dt>{_t('transactions.conversion.source')}:</dt>
                    <dd>{transaction.conversionSource || '-'}</dd>
                  </dl>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>

          {transaction.type === TransactionType.PropPurchase && (
            <CCard>
              <CCardHeader>
                <CCardTitle>{_t('transactions.acquisition-details')}</CCardTitle>
              </CCardHeader>
              <CCardBody>
                <CRow>
                  <CCol md={6}>
                    <dl>
                      <dt>{_t('transactions.utm.source')}:</dt>
                      <dd>{transaction.utmSource || '-'}</dd>

                      <dt>{_t('transactions.utm.medium')}:</dt>
                      <dd>{transaction.utmMedium || '-'}</dd>

                      <dt>{_t('transactions.utm.campaign')}:</dt>
                      <dd>{transaction.utmCampaign || '-'}</dd>
                    </dl>
                  </CCol>
                  <CCol md={6}>
                    <dl>
                      <dt>{_t('transactions.utm.term')}:</dt>
                      <dd>{transaction.utmTerm || '-'}</dd>

                      <dt>{_t('transactions.tracking-campaign')}:</dt>
                      <dd>{transaction.trackingCampaignId || '-'}</dd>

                      <dt>{_t('transactions.utm.content')}:</dt>
                      <dd>{transaction.utmContent || '-'}</dd>
                    </dl>
                  </CCol>
                </CRow>
              </CCardBody>
            </CCard>
          )}

          {transaction.type === TransactionType.CommissionPayout && transaction.affiliateCommission ? (
            <CCard>
              <CCardHeader>
                <CCardTitle>{_t('commissions.commission-details')}</CCardTitle>
              </CCardHeader>
              <CCardBody>
                <CRow>
                  <CCol md={6}>
                    <dl>
                      <dt>{_t('commissions.referral')}:</dt>
                      <dd>
                        {transaction.affiliateCommission.referralName ? (
                          <Link to={`/customers/${transaction.affiliateCommission.referralId}`}>
                            {transaction.affiliateCommission.referralName}
                          </Link>
                        ) : (
                          '-'
                        )}
                      </dd>

                      <dt>{_t('commissions.commission-type')}:</dt>
                      <dd>{transaction.affiliateCommission.commissionType || '-'}</dd>

                      <dt>{_t('commissions.action-type')}:</dt>
                      <dd>{transaction.affiliateCommission.actionType || '-'}</dd>
                    </dl>
                  </CCol>
                  <CCol md={6}>
                    <dl>
                      <dt>{_t('commissions.action-details')}:</dt>
                      <dd>{transaction.affiliateCommission.actionDetails || '-'}</dd>

                      <dt>{_t('global.amount')}:</dt>
                      <dd>{formatCurrency(transaction.amount, transaction.currency)}</dd>

                      <dt>{_t('global.currency')}:</dt>
                      <dd>{transaction.currency}</dd>
                    </dl>
                  </CCol>
                </CRow>
              </CCardBody>
            </CCard>
          ) : null}
        </CCol>
      </CRow>

      {canEditCorrectionComment && (
        <CModal
          show={isEditCommentModalOpen}
          onClose={closeEditCommentModal}
          closeOnBackdrop={!updateTransactionCommentMutation.isLoading}
        >
          <CModalHeader closeButton>
            <CModalTitle>{_t('transactions.edit-transaction-comment')}</CModalTitle>
          </CModalHeader>
          <CModalBody>
            <CFormGroup>
              <CLabel htmlFor="correction-transaction-comment">{_t('global.comment')}</CLabel>
              <CTextarea
                id="correction-transaction-comment"
                value={commentDraft}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setCommentDraft(event.target.value)}
                disabled={updateTransactionCommentMutation.isLoading}
                rows={4}
                maxLength={1000}
              />
            </CFormGroup>
          </CModalBody>
          <CModalFooter>
            <ButtonWrapper
              color="primary"
              onClick={handleCorrectionCommentSave}
              isLoading={updateTransactionCommentMutation.isLoading}
              disabled={isSaveDisabled}
            >
              {_t('action.save')}
            </ButtonWrapper>
            <CButton
              color="light"
              variant="outline"
              onClick={closeEditCommentModal}
              disabled={updateTransactionCommentMutation.isLoading}
            >
              {_t('action.cancel')}
            </CButton>
          </CModalFooter>
        </CModal>
      )}

      {selectedWithdrawal && (
        <UpdateWithdrawalStateModal
          show={Boolean(selectedWithdrawal)}
          onClose={onWithdrawalChangeStateClose}
          actionType={selectedWithdrawal.tableActionType}
          withdrawal={selectedWithdrawal.withdrawal}
        />
      )}

      {/* Qualification Confirmation Modal */}
      <CModal show={isQualifyModalOpen} onClose={() => setQualifyModalOpen(false)}>
        <CModalHeader closeButton>
          <CModalTitle>{_t('qualification.confirm-title')}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p>
            <strong>{_t('global.customer')}:</strong> {transaction.customer?.name || '-'}
          </p>
          <p>
            <strong>{_t('global.amount')}:</strong> {formatCurrency(transaction.amount, transaction.currency)}
          </p>

          <div className="alert alert-danger mt-3">
            <strong>{_t('qualification.warning-title')}</strong>
            <p className="mb-0 mt-2">{_t('qualification.irreversible-warning')}</p>
          </div>
        </CModalBody>
        <CModalFooter>
          <ButtonWrapper color="success" onClick={() => qualifyMutation.mutate()} isLoading={qualifyMutation.isLoading}>
            {_t('qualification.confirm-qualify')}
          </ButtonWrapper>
          <CButton
            color="light"
            variant="outline"
            onClick={() => setQualifyModalOpen(false)}
            disabled={qualifyMutation.isLoading}
          >
            {_t('action.cancel')}
          </CButton>
        </CModalFooter>
      </CModal>
    </PageLayout>
  );
};

export default TransactionPage;
